import React, { useState, useEffect } from 'react';
import { 
  FlaskConical, 
  Sparkles, 
  ShieldCheck, 
  RotateCcw, 
  CheckSquare, 
  UserCheck, 
  ChevronRight, 
  Radio, 
  Clock, 
  Smartphone,
  ExternalLink,
  Zap
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useRouter } from '../../router/RouterContext';
import { QA_CREDENTIALS, resetStagingScenario, ensureStagingTestData } from '../../lib/stagingQaService';
import { getTodayISTDateString } from '../../utils/dateUtils';

interface StagingQaBannerProps {
  onOpenChecklist: () => void;
}

export const StagingQaBanner: React.FC<StagingQaBannerProps> = ({ onOpenChecklist }) => {
  const { user, profile, role, signInWithEmail, logout } = useAuth();
  const { navigate } = useRouter();
  const [isResetting, setIsResetting] = useState(false);
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  const [istTime, setIstTime] = useState<string>('');

  useEffect(() => {
    // Seed QA staging data on mount
    ensureStagingTestData().catch(() => {});

    // Update live IST clock every 10s
    const updateTime = () => {
      try {
        const timeStr = new Intl.DateTimeFormat('en-IN', {
          timeZone: 'Asia/Kolkata',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: true
        }).format(new Date());
        setIstTime(`${timeStr} IST`);
      } catch {
        setIstTime('IST');
      }
    };
    updateTime();
    const interval = setInterval(updateTime, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleQuickLogin = async (targetRole: 'student' | 'teacher' | 'admin') => {
    try {
      const creds = QA_CREDENTIALS[targetRole];
      await logout().catch(() => {});
      await signInWithEmail(creds.email, creds.password);
      if (targetRole === 'admin') navigate('/admin');
      else if (targetRole === 'teacher') navigate('/teacher-app');
      else navigate('/app');
    } catch (err: any) {
      console.warn('[QA Banner] Quick login note:', err?.message);
    }
  };

  const handleReset = async () => {
    setIsResetting(true);
    setResetMessage(null);
    try {
      const res = await resetStagingScenario();
      setResetMessage(res.message || 'Scenario Reset Complete');
      setTimeout(() => setResetMessage(null), 4000);
    } catch (err: any) {
      setResetMessage('Reset failed: ' + err.message);
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="bg-gradient-to-r from-amber-950 via-[#1A1814] to-amber-950 text-amber-200 border-b-2 border-amber-500/40 px-3 py-1.5 text-xs select-none shadow-md z-50">
      <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-2">
        {/* Left: Environment Badge & Time */}
        <div className="flex items-center gap-2.5">
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-amber-500 text-black font-black text-[10px] tracking-wider uppercase animate-pulse">
            <FlaskConical className="w-3 h-3" />
            STAGING / QA
          </span>
          <span className="hidden sm:inline-flex items-center gap-1 text-[11px] text-amber-300/80 font-mono">
            <Clock className="w-3 h-3 text-amber-400" />
            {istTime}
          </span>
          <span className="text-amber-400/50 hidden md:inline">•</span>
          <span className="text-[11px] text-amber-200/70 hidden md:inline">
            Active User: <strong className="text-white font-medium">{user?.email || 'Visitor'}</strong> ({role})
          </span>
        </div>

        {/* Center/Right: Quick 1-Click Role Switchers & Actions */}
        <div className="flex items-center gap-1.5 flex-wrap">
          <div className="flex items-center gap-1 bg-black/40 p-0.5 rounded-lg border border-amber-500/20">
            <button
              onClick={() => handleQuickLogin('student')}
              title="Switch to QA Student (student-test@saremi.academy)"
              className={`px-2 py-0.5 text-[11px] rounded font-medium transition-all ${
                user?.email === QA_CREDENTIALS.student.email
                  ? 'bg-amber-500 text-black font-bold shadow'
                  : 'text-amber-200 hover:bg-white/10'
              }`}
            >
              👨‍🎓 Student
            </button>
            <button
              onClick={() => handleQuickLogin('teacher')}
              title="Switch to QA Teacher (teacher-test@saremi.academy)"
              className={`px-2 py-0.5 text-[11px] rounded font-medium transition-all ${
                user?.email === QA_CREDENTIALS.teacher.email
                  ? 'bg-amber-500 text-black font-bold shadow'
                  : 'text-amber-200 hover:bg-white/10'
              }`}
            >
              👨‍🏫 Teacher
            </button>
            <button
              onClick={() => handleQuickLogin('admin')}
              title="Switch to QA Admin (admin-test@saremi.academy)"
              className={`px-2 py-0.5 text-[11px] rounded font-medium transition-all ${
                user?.email === QA_CREDENTIALS.admin.email
                  ? 'bg-amber-500 text-black font-bold shadow'
                  : 'text-amber-200 hover:bg-white/10'
              }`}
            >
              🛡️ Admin
            </button>
          </div>

          <button
            onClick={handleReset}
            disabled={isResetting}
            title="Reset QA scenario to 4 credits remaining, 0 completed"
            className="flex items-center gap-1 px-2 py-0.5 bg-amber-900/50 hover:bg-amber-800/80 text-amber-300 border border-amber-600/40 rounded text-[11px] font-medium transition-colors disabled:opacity-50"
          >
            <RotateCcw className={`w-3 h-3 ${isResetting ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Reset</span>
          </button>

          <button
            onClick={onOpenChecklist}
            className="flex items-center gap-1.5 px-2.5 py-0.5 bg-amber-500 hover:bg-amber-400 text-black font-bold rounded text-[11px] shadow transition-all hover:scale-105 active:scale-95"
          >
            <CheckSquare className="w-3.5 h-3.5" />
            <span>QA Checklist</span>
          </button>
        </div>
      </div>

      {resetMessage && (
        <div className="text-center py-1 bg-amber-500 text-black font-bold text-xs mt-1 rounded">
          {resetMessage}
        </div>
      )}
    </div>
  );
};
